import React from 'react';
import {
    Alert, Box, Chip, Divider, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, Typography
} from '@mui/material';

const money = value => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const number = value => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
const date = value => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB') : '-';

const Section = ({ title, children }) => (
    <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', mb: 1 }}>{title}</Typography>
        {children}
    </Box>
);

const EffectiveDatedPayrollBreakdown = ({ payroll }) => {
    const segments = payroll?.compensation_segments || payroll?.calculation_audit?.compensation_segments || [];
    const otItems = payroll?.ot_calculation_items || payroll?.calculation_audit?.ot_calculation_items || [];
    const deductions = payroll?.salary_deduction_items || payroll?.calculation_audit?.salary_deduction_items || {};
    const statutory = payroll?.calculation_audit?.statutory_snapshot || {
        pitTaxableEarnings: payroll?.pit_taxable_earnings,
        ssbApplicableEarnings: payroll?.ssb_applicable_earnings,
        ssbBase: payroll?.ssb_contribution_base ?? payroll?.debug?.ssbBase,
        ssbBaseMode: payroll?.debug?.ssbBaseMode,
        ssbEmployee: payroll?.ssb_deduction,
        ssbEmployer: payroll?.ssb_employer,
        pitDeduction: payroll?.tax_deduction
    };
    const deductionItems = [
        ...(deductions.leave || []),
        ...(deductions.absence || []),
        ...(deductions.attendancePenalty || [])
    ];

    return (
        <Stack spacing={3}>
            {segments.length <= 1 && (
                <Alert severity="info">No mid-period compensation change. The normal monthly calculation applies.</Alert>
            )}
            <Section title="Basic salary calculation">
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Effective dates</TableCell><TableCell>Basis</TableCell>
                        <TableCell align="right">Monthly salary</TableCell><TableCell align="right">Units / divisor</TableCell>
                        <TableCell align="right">Calculated pay</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {segments.map((segment, index) => (
                            <TableRow key={`${segment.compensationId || index}-${segment.startDate}`}>
                                <TableCell>{date(segment.startDate)} – {date(segment.endDate)}</TableCell>
                                <TableCell>{segment.basisName || segment.basisType || '-'}</TableCell>
                                <TableCell align="right">{money(segment.monthlySalary)}</TableCell>
                                <TableCell align="right">
                                    {segment.fullPeriodSalary ? 'Full period' : `${number(segment.eligibleUnits)} / ${number(segment.divisorUnits)}`}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>{money(segment.basicPay ?? segment.exactBasicPay)}</TableCell>
                            </TableRow>
                        ))}
                        {!segments.length && <TableRow><TableCell colSpan={5} align="center">No persisted segment detail.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Section>

            {!!otItems.length && <Section title="Date-rated overtime">
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Date</TableCell><TableCell>Category</TableCell><TableCell align="right">Minutes</TableCell>
                        <TableCell align="right">Salary / divisor</TableCell><TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Pay</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{otItems.map((item, index) => <TableRow key={`${item.attendanceSummaryId || index}-${item.category}`}>
                        <TableCell>{date(item.attendanceDate || item.date)}</TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={item.category || '-'} /></TableCell>
                        <TableCell align="right">{number(item.payableMinutes ?? item.minutes)}</TableCell>
                        <TableCell align="right">{money(item.monthlySalary)} / {number(item.divisorDays || item.workingDays || item.divisor)}</TableCell>
                        <TableCell align="right">{money(item.hourlyRate)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{money(item.pay ?? item.exactPay)}</TableCell>
                    </TableRow>)}</TableBody>
                </Table>
            </Section>}

            {!!deductionItems.length && <Section title="Date-rated salary deductions">
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Date</TableCell><TableCell>Source</TableCell><TableCell align="right">Equivalent days</TableCell>
                        <TableCell align="right">Salary / divisor</TableCell><TableCell align="right">Deduction</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{deductionItems.map((item, index) => <TableRow key={`${item.source}-${item.date}-${index}`}>
                        <TableCell>{date(item.date)}</TableCell><TableCell>{String(item.source || '-').replaceAll('_', ' ')}</TableCell>
                        <TableCell align="right">{number(item.equivalentDays)}</TableCell>
                        <TableCell align="right">{money(item.monthlySalary)} / {number(item.divisor)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{money(item.amount ?? item.exactAmount)}</TableCell>
                    </TableRow>)}</TableBody>
                </Table>
            </Section>}

            <Divider />
            <Section title="Combined statutory bases">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        label={`SSB contribution base: ${money(statutory.ssbBase ?? statutory.ssbApplicableEarnings)} MMK`}
                    />
                    <Chip label={`Employee SSB: ${money(statutory.ssbEmployee)} MMK`} color="warning" variant="outlined" />
                    <Chip label={`Employer SSB: ${money(statutory.ssbEmployer)} MMK`} color="warning" variant="outlined" />
                    <Chip label={`PIT taxable: ${money(statutory.pitTaxableEarnings)} MMK`} />
                    <Chip label={`PIT: ${money(statutory.pitDeduction)} MMK`} color="error" variant="outlined" />
                </Stack>
            </Section>
        </Stack>
    );
};

export default EffectiveDatedPayrollBreakdown;
